import React, { useState } from "react";
import { CATEGORIES, DEFAULT_CATEGORY } from "../utils/categories";
import fetchData from "../api/fetchData";

// Small labelled section wrapper so the long form reads as grouped blocks.
const Section = ({ title, hint, children }) => (
    <div className="mb-6">
        <div className="flex items-baseline justify-between mb-3 pb-2 border-b border-gray-200">
            <h2 className="text-xs font-bold uppercase tracking-wide text-gray-500">{title}</h2>
            {hint && <span className="text-xs text-gray-400">{hint}</span>}
        </div>
        {children}
    </div>
);

const Field = ({ label, required, hint, children }) => (
    <div>
        <label className="flex items-center gap-1 text-gray-700 font-medium mb-1 text-sm">
            {label}
            {required && <span className="text-red-500">*</span>}
        </label>
        {children}
        {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
);

const inputClass =
    "w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 text-base transition";

const CreateStorePage = () => {
    const [viewMode, setViewMode] = useState("form"); // 'form' or 'json'
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [copied, setCopied] = useState(false);

    const initialFormData = {
    category: "extras",
        id: "",
        name: "",
        customName: "",
        storeUrl: "",
        affiliateLink: "",
        primaryLocation: "",
        importantLocation: "",
        locations: "",
        description: "",
        longDescription: "",
        metaKeywords: "",
        metaDescription: "",
        aboutContent: "",
        faqs: [],
    };

    const [formData, setFormData] = useState(initialFormData);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFaqChange = (index, field, value) => {
        setFormData((prev) => {
            const faqs = [...(prev.faqs || [])];
            faqs[index] = { ...faqs[index], [field]: value };
            return { ...prev, faqs };
        });
    };

    const addFaq = () => {
        setFormData((prev) => ({
            ...prev,
            faqs: [...(prev.faqs || []), { question: "", answer: "" }],
        }));
    };

    const removeFaq = (index) => {
        setFormData((prev) => ({
            ...prev,
            faqs: (prev.faqs || []).filter((_, i) => i !== index),
        }));
    };

    const toggleView = (mode) => {
        setViewMode(mode);
        setMessage({ type: "", text: "" });
    };

    const isValid = formData.id.trim().length > 0;

    const metaLen = formData.metaDescription.length;
    const metaColor =
        metaLen === 0 ? "text-gray-400" : metaLen <= 160 ? "text-green-600" : "text-red-500";

    const copyJson = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(formData, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            /* clipboard unavailable — no-op */
        }
    };

    const handleSubmit = async () => {
        if (!isValid) {
            setMessage({ type: "error", text: "Store UUID is required." });
            return;
        }
        setLoading(true);
        setMessage({ type: "", text: "" });

        const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
        // Drop incomplete FAQ rows so only complete Q&A pairs are saved.
        const cleanedFaqs = (formData.faqs || [])
            .map((f) => ({
                question: (f.question || "").trim(),
                answer: (f.answer || "").trim(),
            }))
            .filter((f) => f.question && f.answer);
        const payload = {
            ...formData,
            faqs: cleanedFaqs,
            addedOn: now,
            subscribedOn: now
        };

        try {
            await fetchData("/stores/save", null, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            setMessage({ type: "success", text: "Store created successfully!" });
            setFormData(initialFormData);
        } catch (error) {
            console.error("Error saving store:", error);
            setMessage({ type: "error", text: `Failed to create store: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex-1 p-4 md:p-6 bg-gray-50 flex flex-col h-screen overflow-hidden">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4 md:gap-0">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Create Store</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Add a new store with its coupon-page content.</p>
                </div>
                {/* Toggle */}
                <div className="inline-flex rounded-lg bg-gray-200 p-1">
                    <button
                        onClick={() => toggleView("form")}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${viewMode === "form"
                            ? "bg-white text-red-600 shadow-sm"
                            : "text-gray-600 hover:text-gray-800"
                            }`}
                    >
                        Form
                    </button>
                    <button
                        onClick={() => toggleView("json")}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${viewMode === "json"
                            ? "bg-white text-red-600 shadow-sm"
                            : "text-gray-600 hover:text-gray-800"
                            }`}
                    >
                        JSON
                    </button>
                </div>
            </div>

            {/* Message */}
            {message.text && (
                <div
                    className={`p-3 rounded-lg mb-4 text-sm flex items-center gap-2 ${message.type === "error"
                        ? "bg-red-50 text-red-700 border border-red-200"
                        : "bg-green-50 text-green-700 border border-green-200"
                        }`}
                >
                    <span className="font-bold">{message.type === "error" ? "✕" : "✓"}</span>
                    {message.text}
                </div>
            )}

            {/* Card: scrollable body + sticky footer */}
            <div className="bg-white rounded-xl shadow-md flex-1 flex flex-col min-h-0 overflow-hidden border border-gray-100">
                <div className="overflow-y-auto flex-1 p-4 md:p-6">
                    {viewMode === "form" ? (
                        <>
                            <Section title="Store details">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                                    <Field label="Store Slug (ID)" required hint="Unique URL identifier — usually the domain, e.g. nike.com. Used in the page URL /stores/{slug}. Can't be changed later.">
                                        <input
                                            type="text"
                                            name="id"
                                            value={formData.id}
                                            onChange={handleInputChange}
                                            className={`${inputClass} ${!isValid ? "border-red-300" : ""}`}
                                            placeholder="e.g. nike.com"
                                        />
                                    </Field>
                                    <Field label="Category" required hint="Which category page this store is listed on. Its coupons inherit it — coupons have no category of their own.">
                                        <select
                                            name="category"
                                            value={formData.category || DEFAULT_CATEGORY}
                                            onChange={handleInputChange}
                                            className={inputClass}
                                        >
                                            {CATEGORIES.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Store Name" hint="Shown on the coupon page (auto-prettified). Often the domain — set a clean name for a nicer heading.">
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className={inputClass}
                                            placeholder="e.g. Nike"
                                        />
                                    </Field>
                                    <Field label="Custom Name" hint="Optional alternate/display name stored for the store.">
                                        <input
                                            type="text"
                                            name="customName"
                                            value={formData.customName}
                                            onChange={handleInputChange}
                                            className={inputClass}
                                            placeholder="e.g. Nike Official Store"
                                        />
                                    </Field>
                                    <Field label="Store URL">
                                        <input
                                            type="text"
                                            name="storeUrl"
                                            value={formData.storeUrl}
                                            onChange={handleInputChange}
                                            className={inputClass}
                                            placeholder="https://www.example.com"
                                        />
                                    </Field>
                                    <Field label="Affiliate Link">
                                        <input
                                            type="text"
                                            name="affiliateLink"
                                            value={formData.affiliateLink}
                                            onChange={handleInputChange}
                                            className={inputClass}
                                            placeholder="Tracking / affiliate URL"
                                        />
                                    </Field>
                                </div>
                            </Section>

                            <Section title="Location">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
                                    <Field label="Primary Location" hint="Full country, e.g. United States of America">
                                        <input
                                            type="text"
                                            name="primaryLocation"
                                            value={formData.primaryLocation}
                                            onChange={handleInputChange}
                                            className={inputClass}
                                            placeholder="e.g. United States of America"
                                        />
                                    </Field>
                                    <Field label="Locations" hint="Short region code(s), e.g. US or US, UK, CA">
                                        <input
                                            type="text"
                                            name="locations"
                                            value={formData.locations}
                                            onChange={handleInputChange}
                                            className={inputClass}
                                            placeholder="e.g. US"
                                        />
                                    </Field>
                                    <Field label="Important Location" hint="Optional — leave blank if not applicable.">
                                        <input
                                            type="text"
                                            name="importantLocation"
                                            value={formData.importantLocation}
                                            onChange={handleInputChange}
                                            className={inputClass}
                                            placeholder="Optional"
                                        />
                                    </Field>
                                </div>
                            </Section>

                            <Section title="Descriptions & SEO">
                                <div className="space-y-4">
                                    <Field label="Description" hint="Short blurb shown near the top of the coupon page.">
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            className={`${inputClass} resize-y`}
                                            rows="2"
                                            placeholder="Short description of the store"
                                        ></textarea>
                                    </Field>
                                    <Field label="Long Description" hint="Full rich description of the brand. Stored on the store and used when generating About content.">
                                        <textarea
                                            name="longDescription"
                                            value={formData.longDescription}
                                            onChange={handleInputChange}
                                            className={`${inputClass} resize-y`}
                                            rows="5"
                                            placeholder="Full description of the brand, products, and background."
                                        ></textarea>
                                    </Field>
                                    <Field
                                        label="Meta Description"
                                        hint="Shown in search results — aim for ~150–160 characters."
                                    >
                                        <textarea
                                            name="metaDescription"
                                            value={formData.metaDescription}
                                            onChange={handleInputChange}
                                            className={`${inputClass} resize-y`}
                                            rows="2"
                                            placeholder="Enter meta description"
                                        ></textarea>
                                        <div className={`text-right text-xs mt-1 ${metaColor}`}>{metaLen}/160</div>
                                    </Field>
                                    <Field label="Meta Keywords">
                                        <textarea
                                            name="metaKeywords"
                                            value={formData.metaKeywords}
                                            onChange={handleInputChange}
                                            className={`${inputClass} resize-y`}
                                            rows="2"
                                            placeholder="comma, separated, keywords"
                                        ></textarea>
                                    </Field>
                                </div>
                            </Section>

                            <Section title="Coupon page content" hint="Shown in the About & FAQ sections of the store's coupon page">
                                <div className="space-y-4">
                                    <Field label="About content" hint="Blank line separates paragraphs.">
                                        <textarea
                                            name="aboutContent"
                                            value={formData.aboutContent}
                                            onChange={handleInputChange}
                                            className={`${inputClass} resize-y`}
                                            rows="4"
                                            placeholder="About the store — used in the 'About {store}' section of the coupon page."
                                        ></textarea>
                                    </Field>

                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-gray-700 font-medium text-sm">FAQs</label>
                                            <button
                                                type="button"
                                                onClick={addFaq}
                                                className="px-3 py-1.5 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600 transition"
                                            >
                                                + Add FAQ
                                            </button>
                                        </div>
                                        {(formData.faqs || []).length === 0 && (
                                            <p className="text-xs text-gray-400 mb-2 border border-dashed border-gray-200 rounded-lg p-3 text-center">
                                                No FAQs yet. Add a few Q&amp;A pairs to show the FAQ accordion on the coupon page.
                                            </p>
                                        )}
                                        <div className="space-y-2">
                                            {(formData.faqs || []).map((faq, i) => (
                                                <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="inline-flex items-center justify-center h-5 px-2 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold">
                                                            FAQ {i + 1}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeFaq(i)}
                                                            className="text-red-500 hover:text-red-700 text-xs font-medium"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        value={faq.question || ""}
                                                        onChange={(e) => handleFaqChange(i, "question", e.target.value)}
                                                        className="w-full p-2 border border-gray-300 rounded-md mb-2 text-sm focus:outline-none focus:border-red-500"
                                                        placeholder="Question"
                                                    />
                                                    <textarea
                                                        value={faq.answer || ""}
                                                        onChange={(e) => handleFaqChange(i, "answer", e.target.value)}
                                                        rows="2"
                                                        className="w-full p-2 border border-gray-300 rounded-md text-sm resize-y focus:outline-none focus:border-red-500"
                                                        placeholder="Answer"
                                                    ></textarea>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Section>
                        </>
                    ) : (
                        <div className="h-full flex flex-col">
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-gray-700 font-medium text-sm">JSON Payload (read only)</label>
                                <button
                                    type="button"
                                    onClick={copyJson}
                                    className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-200 transition border border-gray-200"
                                >
                                    {copied ? "Copied ✓" : "Copy JSON"}
                                </button>
                            </div>
                            <textarea
                                value={JSON.stringify(formData, null, 2)}
                                readOnly
                                className="w-full border border-gray-200 p-4 rounded-lg font-mono text-sm focus:outline-none flex-1 resize-none bg-gray-50 text-gray-600"
                            ></textarea>
                        </div>
                    )}
                </div>

                {/* Sticky action bar */}
                <div className="border-t border-gray-200 bg-white px-4 md:px-6 py-3 flex items-center justify-between gap-4">
                    <span className="text-xs text-gray-400 hidden sm:block">
                        {isValid ? "Ready to save." : "Store UUID is required to save."}
                    </span>
                    <button
                        onClick={handleSubmit}
                        disabled={loading || !isValid}
                        className={`px-6 py-2.5 bg-red-600 text-white font-semibold rounded-lg shadow-sm hover:bg-red-700 transition flex justify-center items-center w-full sm:w-auto ${loading || !isValid ? "opacity-60 cursor-not-allowed" : ""
                            }`}
                    >
                        {loading && (
                            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        )}
                        {loading ? "Saving…" : "Save Store"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateStorePage;
